import { Vector3, Object3D } from 'three';
import { IEnemy } from '../enemy/IEnemy';

const RADAR_RANGE = 3000;

export class RadarMap {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private size: number;

  constructor() {
    this.canvas = document.getElementById('radar-canvas') as HTMLCanvasElement;
    this.ctx    = this.canvas.getContext('2d')!;
    this.size   = this.canvas.width;
  }

  /**
   * @param player プレイヤーのObject3D（位置と向きを取得）
   * @param enemies 敵一覧
   * @param missilePositions 敵ミサイル位置一覧
   */
  update(player: Object3D, enemies: IEnemy[], missilePositions: Vector3[] = [], playerMissilePositions: Vector3[] = []): void {
    const ctx = this.ctx;
    const s = this.size;
    const cx = s / 2, cy = s / 2;
    const r  = s / 2 - 2;

    // プレイヤーのヨー角（Y軸回転）を取得
    const yaw = player.rotation.y;

    ctx.clearRect(0, 0, s, s);

    // クリッピング（円内だけ描画）
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();

    // 背景
    ctx.fillStyle = 'rgba(0, 20, 0, 0.85)';
    ctx.fillRect(0, 0, s, s);

    // グリッド円
    ctx.strokeStyle = 'rgba(0,255,100,0.2)';
    ctx.lineWidth = 1;
    for (const frac of [0.33, 0.66, 1.0]) {
      ctx.beginPath();
      ctx.arc(cx, cy, r * frac, 0, Math.PI * 2);
      ctx.stroke();
    }

    // 十字線
    ctx.beginPath();
    ctx.moveTo(cx, cy - r); ctx.lineTo(cx, cy + r);
    ctx.moveTo(cx - r, cy); ctx.lineTo(cx + r, cy);
    ctx.stroke();

    // 敵を描画（ヘディングアップ変換）
    for (const e of enemies) {
      if (!e.isAlive) continue;

      // ワールド差分
      const wdx = e.position.x - player.position.x;
      const wdz = e.position.z - player.position.z;
      const dist = Math.sqrt(wdx * wdx + wdz * wdz);
      if (dist > RADAR_RANGE) continue;

      // ヘディングアップ変換（Three.js座標系: 前方=-Z, 右=+X）
      // Ry(yaw) で前方ベクトル = (-sin(yaw), 0, -cos(yaw))
      // 右ベクトル            = ( cos(yaw), 0, -sin(yaw))
      const cos = Math.cos(yaw);
      const sin = Math.sin(yaw);
      // ワールド差分 → プレイヤーローカル
      const localX   =  wdx * cos - wdz * sin;   // 右が正
      const localFwd = -(wdx * sin + wdz * cos);  // 前方が正（-Z前方なので符号反転）

      // 画面Y軸は下が正 → 前方(+localFwd)が上(小さいry)になるよう反転
      const rx = cx + (localX   / RADAR_RANGE) * r * 0.95;
      const ry = cy - (localFwd / RADAR_RANGE) * r * 0.95;

      if (e.isGroundUnit) {
        // 地上敵 = オレンジ丸（地上拠点シンボル）
        ctx.fillStyle = '#ff7744';
        ctx.beginPath();
        ctx.arc(rx, ry, 3, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // 空中敵 = 赤三角（航空機シンボル）
        ctx.fillStyle = '#ff4444';
        ctx.beginPath();
        ctx.moveTo(rx, ry - 4);
        ctx.lineTo(rx + 3.5, ry + 3);
        ctx.lineTo(rx - 3.5, ry + 3);
        ctx.closePath();
        ctx.fill();
      }
    }

    // 敵ミサイルを描画（黄色点滅）
    for (const mpos of missilePositions) {
      const wdx = mpos.x - player.position.x;
      const wdz = mpos.z - player.position.z;
      const dist = Math.sqrt(wdx * wdx + wdz * wdz);
      if (dist > RADAR_RANGE) continue;

      const cos = Math.cos(yaw);
      const sin = Math.sin(yaw);
      const localX   =  wdx * cos - wdz * sin;
      const localFwd = -(wdx * sin + wdz * cos);

      const mx = cx + (localX   / RADAR_RANGE) * r * 0.95;
      const my = cy - (localFwd / RADAR_RANGE) * r * 0.95;

      // 点滅する黄色い菱形
      const blink = Math.sin(Date.now() * 0.012) > 0;
      ctx.fillStyle = blink ? '#ffff00' : '#ffaa00';
      ctx.beginPath();
      ctx.moveTo(mx, my - 4);
      ctx.lineTo(mx + 3, my);
      ctx.lineTo(mx, my + 4);
      ctx.lineTo(mx - 3, my);
      ctx.closePath();
      ctx.fill();
    }

    // プレイヤーミサイルを描画（水色の小点）
    for (const mpos of playerMissilePositions) {
      const wdx = mpos.x - player.position.x;
      const wdz = mpos.z - player.position.z;
      const dist = Math.sqrt(wdx * wdx + wdz * wdz);
      if (dist > RADAR_RANGE) continue;

      const cos = Math.cos(yaw);
      const sin = Math.sin(yaw);
      const localX   =  wdx * cos - wdz * sin;
      const localFwd = -(wdx * sin + wdz * cos);

      const mx = cx + (localX   / RADAR_RANGE) * r * 0.95;
      const my = cy - (localFwd / RADAR_RANGE) * r * 0.95;

      ctx.fillStyle = '#66ccff';
      ctx.beginPath();
      ctx.arc(mx, my, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();

    // 外枠
    ctx.strokeStyle = 'rgba(0,255,100,0.55)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    // 自機（中央・進行方向が常に上向き矢印）
    ctx.save();
    ctx.translate(cx, cy);
    ctx.fillStyle = '#66aaff';
    ctx.strokeStyle = '#aaccff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, -7);    // 先端（前方）
    ctx.lineTo(4, 5);
    ctx.lineTo(0, 2);
    ctx.lineTo(-4, 5);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // ラベル（"FWD" を上端に小さく）
    ctx.fillStyle = 'rgba(0,255,100,0.35)';
    ctx.font = '8px Courier New';
    ctx.textAlign = 'center';
    ctx.fillText('FWD', cx, 12);
  }
}
